import React, { useCallback, useEffect, useState } from 'react';
import { getMetadata, ref as storageRef } from 'firebase/storage';
import { storage } from '../../firebase';
import SourcePreview from './SourcePreview';
import EditorCta from './EditorCta';
import { trackEvent } from '../../analytics';
import { formatBytes } from '../../photoUpload/caps';
import { EMPHASIS_PRIMARY, EMPHASIS_SECONDARY } from './PhotoCompileCta';
import {
    compiledCaptureRange,
    compiledVideoFilename,
    dispositionFilename,
} from '../../utils/videoFilename';

// The project's compiled source video, and the one thing you could not
// previously do with it: take it away. Compiling already charged credits, so
// the download is free — the card says so, because "download" next to a credit
// balance reads like a second charge.
//
// Three states, all of them this one card:
//   A. compiled, up to date  → preview | stats | the Editor CTA, Download
//   B. compiled, stale       → the same, plus an amber "N photos added since"
//                              chip and a Recompile action beside Download
//   C. nothing compiled yet  → a full-width band pitching the compile
//
// The actions column leads with the Editor, not the download: compiling makes
// raw material, and the product wants the next step to be editing it. Download
// is the exit ramp and is styled as one — except under a host that constructs
// this card with no route to the Editor (no shipping surface does), where the
// lead falls back down the column rather than leaving it with no primary at
// all (see `primaryAction`).
//
// The card owns NO compile machinery. `compileCta` is a slot, and what goes in
// it is the one PhotoCompileCta instance the tab already had (in its compact
// `action` variant) — so pricing, preconditions, day scope and the callable
// itself stay in exactly one place.

const DOWNLOAD_ICON = (
    <svg
        className="download-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Firestore hands back a Timestamp, the emulator sometimes a Date, an old doc
// a raw epoch. Anything else is "we don't know when", which is a line we omit
// rather than guess at.
function toDate(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value.toDate === 'function') {
        try {
            const d = value.toDate();
            return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
        } catch (err) {
            return null;
        }
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        // Seconds vs millis: anything below ~1e12 can only be seconds.
        return new Date(value < 1e12 ? value * 1000 : value);
    }
    if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
    return null;
}

// "2 hours ago" while it is still news, an absolute date once it isn't. The
// switch is at a week: past that, "9 days ago" is harder to place than "Aug 20".
function formatCompiledAt(value, now = Date.now()) {
    const date = toDate(value);
    if (!date) return null;
    const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
    if (seconds < 90) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    const sameYear = date.getFullYear() === new Date(now).getFullYear();
    const stamp = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
    return sameYear ? stamp : `${stamp}, ${date.getFullYear()}`;
}

// '1080p' / '4K' — the tier as the compile picker names it, minus the picker's
// "HD" suffix, which is noise in a stat row that already says 1080p.
function resolutionLabel(resolution) {
    if (!resolution) return null;
    return resolution === '4k' ? '4K' : String(resolution);
}

/**
 * Fetch the stored object's metadata once: its byte size (a stat the card
 * shows) and its Content-Disposition (which decides HOW the download runs).
 *
 * Failure is silent and non-blocking by design — a missing size hides one stat,
 * and a missing disposition just routes the download through the blob
 * fallback. Neither is worth an error message on a card whose job is a button.
 */
function useObjectMetadata(storagePath) {
    const [metadata, setMetadata] = useState(null);
    useEffect(() => {
        if (!storagePath) {
            setMetadata(null);
            return undefined;
        }
        let cancelled = false;
        setMetadata(null);
        Promise.resolve()
            .then(() => getMetadata(storageRef(storage, storagePath)))
            .then((meta) => { if (!cancelled) setMetadata(meta || null); })
            .catch(() => { if (!cancelled) setMetadata(null); });
        return () => { cancelled = true; };
    }, [storagePath]);
    return metadata;
}

const CompiledVideoCard = ({
    currentProject,
    coverage = null,
    projectId = null,
    videoUrl = null,
    localVideoMeta = null,
    isPreviewPending = false,
    onSourcePlay,
    onSourceError,
    onRotate = null,
    rotationDisabled = false,
    // photosSinceCompile()'s verdict — {newPhotos, newDays, photosOutsideSelection}
    // or null when there is nothing to compare against. Decides A vs B.
    sinceCompile = null,
    // The tab's single PhotoCompileCta, in whatever shape it decided to render.
    compileCta = null,
    // Project-wide photo total, for the empty state's pitch.
    photoCount = 0,
    // Takes the user to the Edits tab. Null when the host has no tab switcher
    // to offer, in which case the CTA is simply not drawn rather than drawn
    // dead. No shipping host is in that position — ProjectView always passes
    // FootageTab its switcher — so today only a direct construction (the tests)
    // leaves this null.
    onOpenEdits = null,
}) => {
    const video = currentProject?.video || null;
    const compileState = currentProject?.photoCompile || null;
    const storagePath = video?.storagePath || null;
    const metadata = useObjectMetadata(storagePath);

    const filename = compiledVideoFilename(currentProject, coverage);
    // The object already names itself correctly → the browser can stream it
    // straight off the CDN and Firebase's own Content-Disposition supplies the
    // name. (The anchor's `download` attribute is IGNORED cross-origin, so the
    // header is the only thing naming the file on that path.) Anything else —
    // a legacy object uploaded before the header existed, or a project renamed
    // since it was compiled — has to be re-named locally, which means pulling
    // the bytes through a blob first.
    const servedName = dispositionFilename(metadata?.contentDisposition);
    const canStream = !!videoUrl && !!servedName && servedName === filename;

    const handleDownload = useCallback(async () => {
        if (!videoUrl) return;
        trackEvent('source_video_downloaded', {
            project_id: projectId,
            mode: canStream ? 'stream' : 'blob',
        });
        if (canStream) {
            const a = document.createElement('a');
            a.href = videoUrl;
            // Harmless where it is ignored (cross-origin), correct if the URL
            // ever becomes same-origin.
            a.download = filename;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
        }
        try {
            const response = await fetch(videoUrl);
            // A 403/404 body is still a body: without this, an expired or
            // revoked download token would be saved as a few-hundred-byte XML
            // error named like the video. Fail into the catch instead, which
            // hands the URL to the browser and lets IT report the error.
            if (!response.ok) throw new Error(`download failed: ${response.status}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // Blob URLs are same-origin, so `download` is honoured here — this
            // is the whole reason the fallback exists.
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
            window.open(videoUrl, '_blank');
        }
    }, [videoUrl, canStream, filename, projectId]);

    // ---- State C: nothing compiled yet -------------------------------------
    if (!video) {
        return (
            <div className="pv-cv-card-shell" data-testid="compiled-video-card-shell">
                <div
                    className="pv-cv-card pv-cv-card--empty"
                    data-testid="compiled-video-card"
                    data-state="empty"
                >
                    <div className="pv-cv-empty-copy">
                        <h3>Turn your footage into one video</h3>
                        <p>
                            Stitch {photoCount > 0 ? `all ${photoCount.toLocaleString()} photos` : 'your photos'}
                            {' '}into a single video — no editing required.
                        </p>
                    </div>
                    <div className="pv-cv-empty-cta">
                        {compileCta}
                        <p className="pv-cv-note">
                            Takes a few minutes · download it right here when it&apos;s done.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ---- States A and B ----------------------------------------------------
    const newPhotos = sinceCompile?.newPhotos ?? 0;
    const outsidePhotos = sinceCompile?.photosOutsideSelection ?? 0;
    const stale = newPhotos > 0 || outsidePhotos > 0;

    // EXACTLY ONE teal in this column, and this is the single place that decides
    // which button gets it. It goes to the most-encouraged action this card is
    // actually DRAWING:
    //
    //   Editor    — whenever the host handed us a route to the Edits tab.
    //   Recompile — no route, but the CTA slot is filled AND the video is stale,
    //               so that slot is drawing a priced rebuild: the lead this card
    //               had before the route existed.
    //   Download  — no route, and no rebuild in the slot to lead instead.
    //
    // The `compileCta &&` guard is load-bearing, not defensive: the host empties
    // the slot while a compile is queued/processing/failed (FootageTab moves the
    // CTA out to its own progress/retry card) and the video stays stale that
    // whole time, so keying on `stale` alone would award the teal to a button
    // that is not on screen — the no-primary column this fallback exists to
    // prevent.
    //
    // Both fallback branches are unreachable in the app: every shipping host
    // reaches this card through FootageTab, which is only ever rendered with a
    // tab switcher, so `onOpenEdits` is always a function. They cover a host
    // that constructs this card directly (today: the tests) and reproduce the
    // pre-Editor hierarchy.
    const primaryAction = onOpenEdits
        ? 'edits'
        : ((compileCta && stale) ? 'recompile' : 'download');

    // The CTA is an opaque slot, so the card cannot read its markup — but it can
    // tell it how loudly to paint itself. `emphasis` is the whole contract; see
    // PhotoCompileCta's action variant. State C never reaches here, so the empty
    // band's first-compile CTA keeps the default primary/teal.
    const paintedCompileCta = compileCta
        ? React.cloneElement(compileCta, {
            emphasis: primaryAction === 'recompile' ? EMPHASIS_PRIMARY : EMPHASIS_SECONDARY,
        })
        : compileCta;

    const compiledFrom = compileState?.compiledFrom || {};
    const compiledPhotos = typeof compiledFrom.photoCount === 'number'
        ? compiledFrom.photoCount
        : (typeof compileState?.photoCount === 'number' ? compileState.photoCount : null);
    const compiledDays = typeof compiledFrom.dayCount === 'number' ? compiledFrom.dayCount : null;
    const spec = [
        resolutionLabel(compileState?.outputResolution),
        (compileState?.fps || video?.fps) ? `${Math.round(compileState?.fps || video.fps)} fps` : null,
    ].filter(Boolean).join(' · ');
    const sizeBytes = Number(metadata?.size);
    const compiledAt = formatCompiledAt(compileState?.completedAt);
    // The same range the FILE is named after, so the card and the download
    // agree about what is in the video.
    const range = compiledCaptureRange(currentProject, coverage);

    return (
        <div className="pv-cv-card-shell" data-testid="compiled-video-card-shell">
            <div
                className="pv-cv-card"
                data-testid="compiled-video-card"
                data-state={stale ? 'stale' : 'current'}
            >
                <div className="pv-cv-preview">
                    <SourcePreview
                        video={video}
                        videoUrl={videoUrl}
                        rotation={currentProject?.footageRotation}
                        width={(video?.resolution || localVideoMeta?.resolution)?.width}
                        height={(video?.resolution || localVideoMeta?.resolution)?.height}
                        isPreviewPending={isPreviewPending}
                        onPlay={onSourcePlay}
                        onError={onSourceError}
                        onRotate={onRotate}
                        rotateDisabled={rotationDisabled}
                        showRotationNote={false}
                    />
                </div>

                <div className="pv-cv-mid">
                    <div className="pv-cv-title-row">
                        <h3>Compiled video</h3>
                        <span className="pv-cv-chip">Source</span>
                    </div>
                    {/* A labelled two-up grid, not a flowing row. The row put four
                        unlabelled values behind hairline separators, which read as
                        one sentence and — at the widths this card actually gets —
                        wrapped so the last value ("173.8 MB") orphaned onto its own
                        line. A grid says what each number IS and cannot orphan. */}
                    <div className="pv-cv-stats" data-testid="compiled-video-stats">
                        {range && (
                            <div className="pv-cv-stat">
                                <span className="pv-cv-stat-label">Captured</span>
                                <span className="pv-cv-stat-value">{range}</span>
                            </div>
                        )}
                        {compiledPhotos !== null && (
                            <div className="pv-cv-stat">
                                <span className="pv-cv-stat-label">Photos</span>
                                <span className="pv-cv-stat-value">
                                    {compiledPhotos.toLocaleString()}
                                    {compiledDays !== null && ` · ${compiledDays.toLocaleString()} day${compiledDays === 1 ? '' : 's'}`}
                                </span>
                            </div>
                        )}
                        {spec && (
                            <div className="pv-cv-stat">
                                <span className="pv-cv-stat-label">Quality</span>
                                <span className="pv-cv-stat-value">{spec}</span>
                            </div>
                        )}
                        {Number.isFinite(sizeBytes) && sizeBytes > 0 && (
                            <div className="pv-cv-stat">
                                <span className="pv-cv-stat-label">File size</span>
                                <span className="pv-cv-stat-value" data-testid="compiled-video-size">
                                    {formatBytes(sizeBytes)}
                                </span>
                            </div>
                        )}
                    </div>

                    {stale ? (
                        <>
                            {/* Just the timestamp: the range it covers now has its
                                own labelled cell above, and saying it twice on one
                                card is noise, not emphasis. */}
                            <div className="pv-cv-sub">
                                {compiledAt ? `Compiled ${compiledAt}` : 'Compiled earlier'}
                            </div>
                            <div className="pv-cv-chip-row">
                                <span className="pv-cv-amber-chip" data-testid="compiled-video-stale">
                                    {newPhotos > 0
                                        ? `${newPhotos.toLocaleString()} photo${newPhotos === 1 ? '' : 's'} added since this was compiled`
                                        : `${outsidePhotos.toLocaleString()} photo${outsidePhotos === 1 ? ' sits' : 's sit'} on days this video does not cover`}
                                </span>
                            </div>
                        </>
                    ) : (
                        /* Dot FIRST, then one separator style. It used to read
                           "Compiled just now · • up to date · download is free" —
                           an interpunct immediately followed by the status dot,
                           which lands as a doubled separator. The dot is now the
                           bullet the line starts with, and every clause after it
                           is joined the same way. */
                        <div className="pv-cv-sub" data-testid="compiled-video-current">
                            <span className="pv-cv-dot" />
                            <span>
                                Up to date
                                {compiledAt ? ` · compiled ${compiledAt}` : ''}
                                {' · download is free'}
                            </span>
                        </div>
                    )}
                    {onRotate && videoUrl && !isPreviewPending && (
                        <p className="pv-cv-note">
                            Rotation is applied when your video is rendered. Your original
                            files aren&apos;t changed.
                        </p>
                    )}
                </div>

                <div className="pv-cv-actions">
                    {/* FIRST in the column and, whenever it renders at all, its
                        teal lead — `primaryAction` above demotes Download and
                        Recompile to the quiet outline for exactly this reason.
                        The compiled video is raw material, so the step this
                        product wants next is editing it, not filing it away — and the
                        Footage tab named no route to the Edits tab at all until
                        this card did. Leading with it makes the encouraged path
                        the one the eye lands on. The button itself is the shared
                        EditorCta, which the uploaded/combined source card on this
                        same tab also renders — one icon, one geometry, and one
                        state-aware label rule ("Fine-tune in Editor" once a
                        render exists to fine-tune, "Open Editor" before then),
                        matching what OverviewTab says at each of those stages.

                        State C never reaches here: that branch returns above. */}
                    {onOpenEdits && (
                        <div className="pv-cv-edit-row">
                            <EditorCta
                                project={currentProject}
                                onClick={onOpenEdits}
                                testId="compiled-video-edit-cta"
                            />
                        </div>
                    )}
                    <div className="pv-cv-btn-row">
                        {/* The exit ramp. Taking the file away ends the session
                            here, so it stays one click away without outranking
                            whatever leads the column — but it takes the teal
                            back when nothing else is there to lead (see
                            `primaryAction` above). Deliberately NOT `btn ghost`
                            — there is no `ghost` rule in this app, so that class
                            renders the browser's default light button, which
                            would shout louder than the teal above it. */}
                        <button
                            type="button"
                            className={`btn ${primaryAction === 'download' ? 'btn-teal' : 'pv-cv-btn-secondary'} btn-download`}
                            data-testid="compiled-video-download"
                            onClick={handleDownload}
                            disabled={!videoUrl}
                        >
                            {DOWNLOAD_ICON}
                            {stale ? 'Download anyway' : 'Download video'}
                        </button>
                        {paintedCompileCta}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompiledVideoCard;
